import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IUiContentState,
  ListMemberResponse,
  TeamMember,
  UIContentResponse,
} from "./definitions";
import {
  buildEntityCompositeKey,
  getLongNameFromShort,
  getShortNameFromLong,
} from "./utils";
import type { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import { GUEST_SUFFIX } from "utils/guest-label-utils";

export type BaseOption = {
  label: string | undefined;
  value: string | undefined;
  description?: string;
  labelTag?: string;
  country?: string;
};

export const initialState: IUiContentState = {
  countryOptions: [{ label: "No country", value: "No country" }],
  entityOptions: [{ label: "No entity", value: "No entity" }],
  governmentEntityOptions: [
    { label: "No tax authority", value: "No tax authority" },
  ],
  taxTypeOptions: [{ label: "No tax type", value: "No tax type" }],
  ownerOptions: [{ label: "No owner", value: "No owner" }],
  teamAndCurResourceGuestOwnerOptions: [
    { label: "No owner", value: "No owner" },
  ],
  teamAndAllGuestOwnerOptions: [{ label: "No owner", value: "No owner" }],
  criticalOwnerOptions: [{ label: "No owner", value: "No owner" }],
  categoryOptions: [{ label: "No category", value: "No category" }],
  assessmentTypeOptions: [
    { label: "No assessment type", value: "No assessment type" },
  ],
  paymentTypeOptions: [{ label: "No payment type", value: "No payment type" }],
  documentTypeOptions: [
    { label: "No document type", value: "No document type" },
  ],
  securityLevelOptions: [
    { label: "No security level type", value: "No security level type" },
  ],
  taskGroupTypeOptions: [
    { label: "No task group type", value: "No task group type" },
  ],
  inquiryTypeOptions: [{ label: "No type", value: "No type" }],
  subInquiryTypeOptions: [{ label: "No subtype", value: "No subtype" }],
  complexityOptions: [
    { label: "No complexity type", value: "No complexity type" },
  ],
  priorityOptions: [{ label: "No priority type", value: "No priority type" }],
  auditStatusOptions: [{ label: "No status", value: "No status" }],
  taskStatusOptions: [{ label: "No task status", value: "No task status" }],
  taxFunctionalAreaOptions: [
    { label: "No tax functional area", value: "No tax functional area" },
  ],
  penaltyRiskLevelOptions: [
    { label: "No penalty/risk level", value: "No penalty/risk level" },
  ],
  agentStatusOptions: [{ label: "No agent status", value: "No agent status" }],
  actionOwnerOptions: [
    { label: "No Action pending", value: "No Action pending" },
  ],
  marketplaceIdOptions: [
    { label: "No marketplace id", value: "No marketplace id" },
  ],
};

export const mapOptions = (values: Array<string>) => {
  return values
    .map((value: string) => ({
      label: value,
      value: value,
    }))
    .sort((a: { label: string }, b: { label: string }) => {
      if (a.label === "\u00A0") return -1;
      if (b.label === "\u00A0") return 1;
      return a.label > b.label ? 1 : -1;
    });
};

export const mapMembers = (values: Array<TeamMember>) => {
  return values
    .map((value: TeamMember) => ({
      label: value.alias,
      value: value.alias,
    }))
    .sort((a: { label: string }, b: { label: string }) =>
      a.label > b.label ? 1 : -1,
    );
};

const mapAndCombineMembers = (response: ListMemberResponse) => {
  const members = response.members.map((value: TeamMember) => ({
    label: value.alias,
    value: value.alias,
  }));
  const guests = mapGuests(response.guests) || [];
  return mergeMembersAndGuests(members, guests);
};

export const mapGuests = (
  values: Array<TeamMember>,
): Array<SelectProps.Option> => {
  return values.map((value: TeamMember) => ({
    label: `${value.alias}${GUEST_SUFFIX}`,
    value: value.alias,
  }));
};

export const sortMembers = (
  values: Array<SelectProps.Option>,
): Array<SelectProps.Option> => {
  return values.sort((a: SelectProps.Option, b: SelectProps.Option) =>
    (a.label ?? "") > (b.label ?? "") ? 1 : -1,
  );
};

// Merge team-member options with guest options, dropping guest entries whose
// alias is already present as a team member. Team membership supersedes a
// stale guest permission record, so the unsuffixed entry wins.
export const mergeMembersAndGuests = (
  members: Array<SelectProps.Option>,
  guests: Array<SelectProps.Option>,
): Array<SelectProps.Option> => {
  const memberValues = new Set(members.map((m) => m.value));
  const dedupedGuests = guests.filter((g) => !memberValues.has(g.value));
  return sortMembers([...members, ...dedupedGuests]);
};

const mapCriticalMembers = (values: Array<TeamMember>) => {
  return values
    .filter((value: TeamMember) => value.isCritical)
    .map((value: TeamMember) => ({
      label: value.alias,
      value: value.alias,
    }))
    .sort((a: { label: string }, b: { label: string }) =>
      a.label > b.label ? 1 : -1,
    );
};

const mapOptionsUnsort = (values: Array<string>) => {
  return values.map((value: string) => ({
    label: value,
    value: value,
  }));
};

const mapCountryOptions = (values: Array<string>) => {
  return values.map((inputValue: string) => {
    const longName = getLongNameFromShort(inputValue);
    if (longName !== inputValue) {
      return {
        label: longName,
        value: inputValue,
        description: inputValue,
      };
    }
    const shortName = getShortNameFromLong(inputValue);
    return {
      label: inputValue,
      value: shortName,
      description: shortName,
    };
  });
};

const mapOptionsWithGroup = (values: Array<BaseOption>) => {
  const items: { label: string | undefined; options: BaseOption[] }[] = [];
  const groupNames = new Set(values.map((value) => value.labelTag));
  groupNames.forEach((groupName) => {
    const entries: Array<BaseOption> = [];
    values.forEach((value) => {
      if (value.labelTag === groupName) {
        const entry = {
          label: value.label ?? "",
          value: value.value ?? "",
          description: value.value ?? "",
          country: value.country ?? "",
        };

        entries.push(entry);
      }
    });
    entries.sort((a: BaseOption, b: BaseOption) => {
      if (a.label === undefined) return 1;
      if (b.label === undefined) return -1;

      return a.label > b.label ? 1 : -1;
    });
    const group = {
      label: groupName,
      options: entries,
    };

    items.push(group);
  });
  return items;
};

const mapOptionsObj = (values: Array<BaseOption>) => {
  return values
    .map((value: BaseOption) => ({
      label: value.label ?? "",
      value: value.value ?? "",
      description: value.value ?? "",
    }))
    .sort((a: { label: string }, b: { label: string }) =>
      a.label > b.label ? 1 : -1,
    );
};

const mapOptionsLayerUnsort = (values: Array<BaseOption>) => {
  const items = Array.from(
    values.reduce((groups, value) => {
      const group = groups.get(value.label) || [];
      group.push({ label: value.value, value: value.value });
      groups.set(value.label, group);
      return groups;
    }, new Map<string | undefined, BaseOption[]>()),
    ([label, options]) => ({ label, options }),
  );
  return items;
};

const formatEntityGlobalOptions = (entityGlobal: any[]) => {
  return entityGlobal.map((entity) => {
    // Build entityCode in format "code" or "code - registrationNumber"
    const entityCode = entity.registrationNumber // itx compliance special case
      ? `${entity.code} - ${entity.registrationNumber}`
      : entity.code;
    const compositeKey = buildEntityCompositeKey(entity.name, entityCode);

    return {
      label: entity.name,
      value: compositeKey, // value should be unique in a option list
      description: entityCode,
    };
  });
};

// Applies a uicontent payload onto a UI content object (Immer draft or plain copy).
export const applyUiContent = (
  target: IUiContentState,
  content: string | null | undefined,
) => {
  if (!content) {
    return;
  }
  const uiContent = JSON.parse(content);
  if (uiContent.country !== undefined)
    target.countryOptions = mapCountryOptions(uiContent.country);

  if (
    uiContent.entityGlobal1 !== undefined &&
    uiContent.entityGlobal2 !== undefined
  ) {
    const allEntityOptions: any[] = [];

    allEntityOptions.push(
      ...formatEntityGlobalOptions(uiContent.entityGlobal1),
    );
    allEntityOptions.push(
      ...formatEntityGlobalOptions(uiContent.entityGlobal2),
    );

    target.entityOptions = allEntityOptions;
  }

  if (uiContent.governmententity !== undefined)
    target.governmentEntityOptions = mapOptionsWithGroup(
      uiContent.governmententity,
    );

  if (uiContent.taxtype !== undefined)
    target.taxTypeOptions = mapOptionsUnsort(uiContent.taxtype);
  if (uiContent.category !== undefined)
    target.categoryOptions = mapOptions(uiContent.category);
  if (uiContent.assessmenttype !== undefined)
    target.assessmentTypeOptions = mapOptionsUnsort(uiContent.assessmenttype);
  if (uiContent.paymenttype !== undefined)
    target.paymentTypeOptions = mapOptions(uiContent.paymenttype);
  if (uiContent.documenttype !== undefined)
    target.documentTypeOptions = mapOptionsUnsort(uiContent.documenttype);
  if (uiContent.securitylevel !== undefined)
    target.securityLevelOptions = mapOptions(uiContent.securitylevel);
  if (uiContent.taskgrouptype !== undefined)
    target.taskGroupTypeOptions = mapOptions(uiContent.taskgrouptype);
  if (uiContent.inquirytype !== undefined)
    target.inquiryTypeOptions = mapOptions(uiContent.inquirytype);
  if (uiContent.subinquirytype !== undefined)
    target.subInquiryTypeOptions = mapOptionsLayerUnsort(
      uiContent.subinquirytype,
    );
  if (uiContent.complexity !== undefined)
    target.complexityOptions = mapOptionsUnsort(uiContent.complexity);
  if (uiContent.priority !== undefined)
    target.priorityOptions = mapOptionsUnsort(uiContent.priority);
  if (uiContent.auditstatus !== undefined)
    target.auditStatusOptions = mapOptionsUnsort(uiContent.auditstatus);
  if (uiContent.taskstatus !== undefined)
    target.taskStatusOptions = mapOptionsUnsort(uiContent.taskstatus);
  if (uiContent.taxFunctionalArea !== undefined)
    target.taxFunctionalAreaOptions = mapOptionsUnsort(
      uiContent.taxFunctionalArea,
    );
  if (uiContent.penaltyRiskLevel !== undefined)
    target.penaltyRiskLevelOptions = mapOptionsUnsort(
      uiContent.penaltyRiskLevel,
    );
  if (uiContent.agentStatus !== undefined)
    target.agentStatusOptions = mapOptionsUnsort(uiContent.agentStatus);
  if (uiContent.actionOwner !== undefined)
    target.actionOwnerOptions = mapOptionsUnsort(uiContent.actionOwner);
  if (uiContent.marketplaceId !== undefined)
    target.marketplaceIdOptions = mapOptionsObj(uiContent.marketplaceId);
  if (uiContent.defaultcountry !== undefined)
    target.defaultCountry = uiContent.defaultcountry[0];
  if (uiContent.suffixField !== undefined)
    target.suffixField = uiContent.suffixField[0];
  if (
    uiContent.global1 !== undefined &&
    uiContent.global2 !== undefined &&
    uiContent.global3 !== undefined
  ) {
    const combinedGlobal: any = {};

    if (uiContent.global1 !== undefined) {
      Object.assign(combinedGlobal, uiContent.global1);
    }
    if (uiContent.global2 !== undefined) {
      Object.assign(combinedGlobal, uiContent.global2);
    }
    if (uiContent.global3 !== undefined) {
      Object.assign(combinedGlobal, uiContent.global3);
    }

    // 1. Process country options from combined global content
    const countriesFromGlobal = Object.keys(combinedGlobal);
    const globalCountryOptions = countriesFromGlobal.map(
      (shortName: string) => ({
        label: combinedGlobal[shortName].longName,
        value: shortName,
        description: shortName,
      }),
    );

    // 2. Process government entity options with country references
    const allGovernmentEntities: BaseOption[] = [];
    Object.keys(combinedGlobal).forEach((shortName: string) => {
      const country = combinedGlobal[shortName];
      if (country.governmententity && Array.isArray(country.governmententity)) {
        country.governmententity.forEach((entity: any) => {
          allGovernmentEntities.push({
            label: entity.label,
            value: entity.value,
            labelTag: entity.labelTag,
            country: shortName,
          });
        });
      }
    });

    // 3. Create currency mapping
    const currencyMapping: Record<string, string> = {};
    const currencyCodeMapping: Record<string, string> = {};
    const nationalBankRateTypeMapping: Record<string, string> = {};
    Object.keys(combinedGlobal).forEach((shortName: string) => {
      currencyMapping[shortName] = combinedGlobal[shortName].currencySign;
      if (combinedGlobal[shortName].currencyCode) {
        currencyCodeMapping[shortName] = combinedGlobal[shortName].currencyCode;
      }
      if (combinedGlobal[shortName].nationalBankRateType) {
        nationalBankRateTypeMapping[shortName] =
          combinedGlobal[shortName].nationalBankRateType;
      }
    });

    // 4. Overwrite options with global content
    target.countryOptions = globalCountryOptions;
    target.governmentEntityOptions = mapOptionsWithGroup(allGovernmentEntities);
    target.countryToCurrencyMap = currencyMapping;
    target.countryToCurrencyCodeMap = currencyCodeMapping;
    target.countryToNationalBankRateTypeMap = nationalBankRateTypeMapping;
  }
};

// Shared reducer factory function to eliminate code duplication
const createUiContentReducers = () => ({
  uiContentLoaded: (
    state: IUiContentState,
    action: PayloadAction<UIContentResponse>,
  ) => {
    applyUiContent(state, action.payload.content);
  },
  teamMemberLoaded: (
    state: IUiContentState,
    action: PayloadAction<ListMemberResponse>,
  ) => {
    if (action.payload.members === null) {
      return;
    }
    state.ownerOptions = mapMembers(action.payload.members);
    state.criticalOwnerOptions = mapCriticalMembers(action.payload.members);
    state.teamAndAllGuestOwnerOptions = mapAndCombineMembers(action.payload);
  },
  // this is the combined owner options of team members and all the guests to the resource that user is viewing in this team
  combinedOwnerOptionsLoaded: (
    state: IUiContentState,
    action: { payload: SelectProps.Option[] },
  ) => {
    state.teamAndCurResourceGuestOwnerOptions = action.payload;
  },
});

// Original UI content slice using shared reducer factory
export const uiContentSlice = createSlice({
  name: "uiContent",
  initialState,
  reducers: createUiContentReducers(),
});

export const { uiContentLoaded, teamMemberLoaded, combinedOwnerOptionsLoaded } =
  uiContentSlice.actions;

// Litigation UI content slice using the same shared reducer factory
export const litigationUiContentSlice = createSlice({
  name: "litigationUiContent",
  initialState,
  reducers: createUiContentReducers(),
});

export const {
  uiContentLoaded: litigationUiContentLoaded,
  teamMemberLoaded: litigationTeamMemberLoaded,
  combinedOwnerOptionsLoaded: litigationCombinedOwnerOptionsLoaded,
} = litigationUiContentSlice.actions;

// Notice UI content slice using the same shared reducer factory
export const noticeUiContentSlice = createSlice({
  name: "noticeUiContent",
  initialState,
  reducers: createUiContentReducers(),
});

export const {
  uiContentLoaded: noticeUiContentLoaded,
  teamMemberLoaded: noticeTeamMemberLoaded,
  combinedOwnerOptionsLoaded: noticeCombinedOwnerOptionsLoaded,
} = noticeUiContentSlice.actions;

export default uiContentSlice.reducer;
export const litigationUiContentReducer = litigationUiContentSlice.reducer;
export const noticeUiContentReducer = noticeUiContentSlice.reducer;
